import React, { useCallback, useContext, useEffect } from "react"
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom"

import { customPalette } from "../../../../../../../../../theme"
import { CustomBottomNavigation } from "../../../../../../../../components/buttons/navigate-button/CustomBottomNavigation"
import { IconInbox } from "../../../../../../../../components/custom-icons/icon-inbox"
import { TabEnum } from "../../../../../../../../components/tabs/tabs"
import { pagesUrl } from "../../../../../../../../core/appConstants"
import { BSModelFileContext } from "../../../../../../../../core/context/beem-shot/BSBimModel/BSBimModelFileContext"
import { AppNavBarContext, NavbarTypeEnum } from "../../../../../../../../core/context/nav-bar/AppNavBarContext"
import { CoverImgProjectContext } from "../../../../../../../../core/context/project/CoverImgProjectContext"
import { ProjectContext } from "../../../../../../../../core/context/project/ProjectContext"
import { AppSideBarContext } from "../../../../../../../../core/context/AppSideBarContext/AppSideBarContext"
import { resolveUrl } from "../../../../../../../../core/services/http-service"
import { BimModelFormContext } from "../../context/BimModelFormContext"
import { BSUploadModelTab } from "./tabs/BSUploadModelTab"

interface IProps {
  goToControlStep(): void
}

export function BSBimModelForm({ goToControlStep }: Readonly<IProps>): React.JSX.Element {
  const navigate = useNavigate()
  const location = useLocation()
  const { projectId, bsBimModelId } = useParams()
  const [searchParams] = useSearchParams()

  const { setTypeNavBar, setPreviousUrl, setStepHeader, setActionOnBackButton, setImageFile, setIcon, setTitle } =
    useContext(AppNavBarContext)
  const { setShowAppSide } = useContext(AppSideBarContext)

  const { isModelFileLoading } = useContext(BSModelFileContext)
  const { isModelUpdateRef, isSurfaceUpdated, handleSubmitBSModelForm, validateForm } = useContext(BimModelFormContext)
  const { file } = useContext(CoverImgProjectContext)
  const { project } = useContext(ProjectContext)

  const navigateBack: () => void = useCallback(() => {
    const bsVariantId = searchParams.get("bsVariantId")
    const bsBuildingId = searchParams.get("bsBuildingId")

    if (bsVariantId && bsBuildingId) {
      navigate(resolveUrl(pagesUrl.BUILDING_CHOICE, [projectId, bsVariantId, bsBuildingId]))
    } else if (!location.state?.from.endsWith("/detail")) {
      navigate(resolveUrl(pagesUrl.PROJECT_PAGE_DETAIL, [projectId], { tab: TabEnum.PROJECT_BIM_MODEL }))
    } else {
      navigate(resolveUrl(pagesUrl.BIM_MODEL_IMPORT_PAGE, [projectId, bsBimModelId]))
    }
  }, [searchParams, location.state?.from, navigate, projectId, bsBimModelId])

  useEffect(() => {
    setTypeNavBar(NavbarTypeEnum.STEP_HEADER)
    setActionOnBackButton(navigateBack)
    setImageFile(file)
    setIcon(<IconInbox sx={{ color: customPalette.black, fontWeight: "2px" }} />)
    setStepHeader(0)
    setShowAppSide(false)
    setTitle(project?.name ?? "")
  }, [
    setTypeNavBar,
    setPreviousUrl,
    setActionOnBackButton,
    navigateBack,
    setStepHeader,
    setImageFile,
    file,
    setIcon,
    setTitle,
    project?.name,
    setShowAppSide,
  ])

  const cancelButtonAction = useCallback(() => {
    navigateBack()
  }, [navigateBack])

  const getBottomButtonLabel = useCallback(() => {
    if (isModelUpdateRef.current) {
      return "Suivant"
    } else {
      return "Valider"
    }
  }, [isModelUpdateRef])

  const onNextButton = useCallback(() => {
    const isFormValid = validateForm()
    if (!isFormValid) {
      // If the form is invalid, stay on the page
      return
    }

    if (isModelUpdateRef.current) {
      // Model has been modified, go to control
      goToControlStep()
    } else if (!isModelUpdateRef.current && isSurfaceUpdated.current) {
      // Model has not been modified but surface were, submit directly
      handleSubmitBSModelForm().then(() => {
        navigateBack()
      })
    } else if (!isModelUpdateRef.current && !isSurfaceUpdated.current) {
      // Nothing changed. TODO: add navigate back
      navigateBack()
    }
  }, [goToControlStep, handleSubmitBSModelForm, isModelUpdateRef, isSurfaceUpdated, navigateBack, validateForm])

  return (
    <>
      <BSUploadModelTab />

      <CustomBottomNavigation
        actionLabel={getBottomButtonLabel()}
        validateAction={onNextButton}
        cancelLabel="Annuler"
        cancelAction={cancelButtonAction}
        isSubmitting={isModelFileLoading}
      />
    </>
  )
}
